/**
 * The Around the Site chapter — slide content (data only, no JSX).
 *
 * Each slide pairs a real, PIL-annotated screenshot from the live app
 * (/public/how-it-works/img-site) with a numbered legend. The legend badge
 * numbers correspond to the badges baked into the screenshot by
 * qa/how-it-works/annotate.py — badge 1 is always the "start here" (amber)
 * anchor of the page. Keep callout text in sync with annotations.json.
 */
import type { TourSlideBase } from './tourCore';

export interface SiteSlideDef extends TourSlideBase {
  /** Big display headline. */
  title: string;
  /** Optional one-line intro above the callout list. */
  intro?: string;
  /** Numbered callouts matching the badges baked into the screenshot. */
  callouts: { n: number; label: string; detail: string }[];
}

export const SITE_SLIDES: SiteSlideDef[] = [
  {
    id: 'dashboard',
    kicker: 'Home base',
    title: 'The Dashboard.',
    intro: 'The first thing you see after signing in — the lab at a glance.',
    image: '/how-it-works/img-site/dashboard.webp',
    alt: 'Annotated screenshot of the ALIS dashboard',
    accent: 'sage',
    callouts: [
      { n: 1, label: 'Top navigation', detail: 'Projects, Quotes, Schedule, Clients — every corner of the app is one click from here.' },
      { n: 2, label: 'Global search', detail: 'Ctrl+K from anywhere. Projects, samples, work orders — just start typing.' },
      { n: 3, label: 'Live overview cards', detail: 'What is active, what is due, what needs attention today.' },
      { n: 4, label: 'Version chip', detail: 'Click it to see what shipped this week — it changes often.' },
    ],
  },
  {
    id: 'projects',
    kicker: 'Where jobs live',
    title: 'Projects.',
    image: '/how-it-works/img-site/projects.webp',
    alt: 'Annotated screenshot of the projects list',
    accent: 'moss',
    callouts: [
      { n: 1, label: 'The project list', detail: 'Every project with client, status and dates — sortable, filterable, searchable.' },
      { n: 2, label: 'Filters', detail: 'Narrow by status, client or your own text search.' },
      { n: 3, label: 'New Project', detail: 'One dialog: name it, pick the client and site, go.' },
    ],
  },
  {
    id: 'quotes-list',
    kicker: 'Door one',
    title: 'Quotes.',
    intro: 'Where most work enters the building.',
    image: '/how-it-works/img-site/quotes-list.webp',
    alt: 'Annotated screenshot of the quotes list',
    accent: 'amber',
    callouts: [
      { n: 1, label: 'Every quote, every status', detail: 'Draft, awaiting approval, sent, accepted — the whole pipeline in one table.' },
      { n: 2, label: 'Pending approvals', detail: 'Quotes waiting on an approver surface here so nothing stalls.' },
      { n: 3, label: 'New Quote', detail: 'Start from the catalog — real prices, real tests.' },
    ],
  },
  {
    id: 'quote-editor',
    kicker: 'Building a quote',
    title: 'The Quote editor.',
    image: '/how-it-works/img-site/quote-editor.webp',
    alt: 'Annotated screenshot of the quote editor',
    accent: 'amber',
    callouts: [
      { n: 1, label: 'One quote, five sections', detail: 'Header, Pricing, Scope & Notes, Terms, Activity — line items come straight from the catalog.' },
      { n: 2, label: 'Approval → Accepted', detail: 'Approved internally, then one click — Mark as Accepted — turns it into a work order.' },
      { n: 3, label: 'PDF, proposal & send', detail: 'Preview the polished PDF, generate a proposal, send to the client with an accept link.' },
      { n: 4, label: 'Already scheduled', detail: 'This quote carries a draft schedule before the client even says yes — synced as the quote changes.' },
    ],
  },
  {
    id: 'master-gantt',
    kicker: 'The big board',
    title: 'Schedule → Master Gantt.',
    intro: 'Everything the lab has committed to, on one timeline.',
    image: '/how-it-works/img-site/master-gantt.webp',
    alt: 'Annotated screenshot of the master Gantt schedule',
    accent: 'slate',
    callouts: [
      { n: 1, label: 'Schedule views', detail: 'Master Gantt, Machine Schedule, Employee Schedule, Skill Matrix, My Schedule — pick your lens.' },
      { n: 2, label: 'Project timelines', detail: 'Every project and work order as bars — committed work solid, proposals hatched.' },
      { n: 3, label: 'Today line', detail: 'The blue line is now. Red dashes mark each project’s critical path.' },
    ],
  },
  {
    id: 'scenario-planner',
    kicker: 'Where plans are made',
    title: 'The Scenario Planner.',
    image: '/how-it-works/img-site/scenario-planner.webp',
    alt: 'Annotated screenshot of the scenario planner',
    accent: 'slate',
    callouts: [
      { n: 1, label: 'Draft scenarios', detail: 'Plan in a sandbox — nothing touches the live schedule until you commit.' },
      { n: 2, label: 'Task & machine grid', detail: 'Drag bars, split across machines, watch conflicts light up as you go.' },
      { n: 3, label: 'People & capacity', detail: 'Assign by skill level; the heatmap shows who is over 100% before it happens.' },
      { n: 4, label: 'Commit', detail: 'Happy? Submit — tentative bookings flip to committed and the whole lab sees them.' },
    ],
  },
  {
    id: 'work-order',
    kicker: 'The heart of the job',
    title: 'A Work Order, up close.',
    image: '/how-it-works/img-site/work-order.webp',
    alt: 'Annotated screenshot of a work order detail page',
    accent: 'sage',
    callouts: [
      { n: 1, label: 'The sample grid', detail: 'Every sample with its location, depth and condition — edit inline, upload CSVs in bulk.' },
      { n: 2, label: 'Tests per sample', detail: 'Attach tests to samples; each becomes a row the lab can schedule and run.' },
      { n: 3, label: 'Schedule link', detail: 'See the plan this work order feeds, sync changes, or commit its schedule from right here.' },
    ],
  },
  {
    id: 'test-workflow',
    kicker: 'Running a test',
    title: 'The Test workspace.',
    intro: 'Parameters, readings, analysis and photos — one tabbed flow per test.',
    image: '/how-it-works/img-site/test-workflow.webp',
    alt: 'Annotated screenshot of the test workflow page',
    accent: 'moss',
    callouts: [
      { n: 1, label: 'Test tabs', detail: 'Parameters → Result Entry → Analysis & Plots → Photos. The workflow walks left to right.' },
      { n: 2, label: 'Structured data entry', detail: 'Parameters, per-stage readings, Leeb hardness — calculations and curves update as data lands.' },
      { n: 3, label: 'Save & Generate', detail: 'One click renders the full PDF in the lab’s shared house style.' },
    ],
  },
  {
    id: 'reports',
    kicker: 'The finish line',
    title: 'Reports.',
    image: '/how-it-works/img-site/reports.webp',
    alt: 'Annotated screenshot of report generation',
    accent: 'amber',
    callouts: [
      { n: 1, label: 'Project Tests tab', detail: 'Every test in the project with its status — generate reports one at a time or in bulk.' },
      { n: 2, label: 'One house style', detail: 'Every report — Direct Shear to Cerchar — comes out of the same engine, pixel-consistent.' },
      { n: 3, label: 'Bulk ZIP', detail: 'Select everything and download the whole project’s reports in one archive.' },
    ],
  },
  {
    id: 'feedback',
    kicker: 'Always listening',
    title: 'The Feedback button follows you everywhere.',
    intro: 'Bottom-right corner, every single page.',
    image: '/how-it-works/img-site/feedback.webp',
    alt: 'Annotated screenshot highlighting the feedback button',
    accent: 'night',
    callouts: [
      { n: 1, label: 'One click to tell us', detail: 'Bug, idea, question — it captures the page you were on and what you were doing.' },
      { n: 2, label: 'Track your tickets', detail: 'My Feedback in the user menu shows every ticket and what happened to it.' },
      { n: 3, label: 'It actually ships', detail: 'Feedback becomes fixes in the weekly release — watch the version chip.' },
    ],
  },
];
